import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";
import type { SparkPoint } from "./state-of-play";

export interface DisciplineRow {
  scope: { division: string; industry?: string; grade?: string; rep?: string };
  targetYield: number;
  actualYield: number;
  /** actualYield - targetYield; negative means underpriced against target. */
  deviation: number;
  overrideCount: number;
  /** As captured in Salesforce, once that feed exists. */
  overrideReasons: string[];
  dealCount: number;
  /** Approved internal commission rate for this scope, in percent of gross margin. */
  targetInternalCommissionRate: number;
  /** What was actually paid out, as a percent of gross margin. */
  actualInternalCommissionRate: number;
  /** actual - target; positive means commission leakage above policy. */
  commissionRateDeviation: number;
}

export interface DisciplineSummary {
  adherenceRate: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  avgDeviation: { current: number; previousPeriod: number };
  overrideCount: { current: number; sparkline: SparkPoint[] };
  byScope: DisciplineRow[];
  trend: Array<{ month: string; adherenceRate: number }>;
}

export type DisciplineLens = "By segment" | "By rep";

/** Collapses byScope to one bar per office or per industry, aggregating the
 * other dimension(s) away entirely rather than just hiding them. `lens` still
 * applies underneath as a row-population filter (deals with a real industry
 * vs. a real rep) -- see app/repository/discipline.py. */
export type DisciplineView = "Office" | "Industry";

export const DISCIPLINE_CAPTION =
  "Reads actual charged yield from Salesforce once that feed is connected — no data yet.";

export function scopeLabel(scope: DisciplineRow["scope"]) {
  if (scope.rep) return `${scope.rep} · ${scope.division}`;
  return [scope.division, scope.industry, scope.grade].filter(Boolean).join(" · ");
}

export const disciplineQueryKeys = {
  summary: (lens: DisciplineLens, view?: DisciplineView, ticket?: TicketSize) =>
    ["discipline", "summary", lens, view ?? "detail", ticket ?? "all"] as const,
};

export function useDisciplineSummary(lens: DisciplineLens, view?: DisciplineView) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: disciplineQueryKeys.summary(lens, view, ticket),
    queryFn: () => apiGet<DisciplineSummary>("/discipline", { lens, view, ticket }),
  });
}
